<template>
  <nav :aria-label="$t('ux.molecules.inspirationPagination.ariaLabel')" aria-live="polite" aria-relevant="text" class="Inspiration-pagination">
    <ul class="Inspiration-pagination__list">
      <li class="Inspiration-pagination__list-item">
        <ux-atoms-link
          :aria-current="undefined"
          :disabled="currentPage === 1"
          :href="previousPage"
          @click.prevent="goToPreviousPage"
          class="Button Button--outlined Inspiration-pagination__link Inspiration-pagination__link--first-last"
          classic
        >
          <span class="accessibility-sr-only">
            {{ $t('ux.molecules.inspirationPagination.goToPrevious') }}
          </span>
          <ux-atoms-icon :name="'chevron-left'" size="m" />
        </ux-atoms-link>
      </li>

      <li
        :class="['Inspiration-pagination__list-item', { 'Inspiration-pagination__list-item--current': Number(page) === currentPage }]"
        :key="index"
        v-for="(page, index) in pagination"
      >
        <ux-atoms-link
          :aria-current="currentPage === page ? 'page' : undefined"
          :href="thisPage(Number(page))"
          @click.prevent="goToPage($event, Number(page))"
          class="Button Button--outlined Inspiration-pagination__link"
          classic
          v-if="Number.isInteger(page)"
        >
          <span class="accessibility-sr-only">
            {{ $t('ux.molecules.inspirationPagination.page') }}
          </span>
          {{ page }}
        </ux-atoms-link>

        <ux-atoms-typo as="p" v-else>
          {{ page }}
        </ux-atoms-typo>
      </li>

      <li>
        <ux-atoms-link
          :aria-current="undefined"
          :disabled="currentPage === totalPage"
          :href="nextPage"
          @click.prevent="goToNextPage"
          class="Button Button--outlined Inspiration-pagination__link Inspiration-pagination__link--first-last"
          classic
        >
          <span class="accessibility-sr-only">
            {{ $t('ux.molecules.inspirationPagination.goToNext') }}
          </span>
          <ux-atoms-icon :name="'chevron-right'" size="m" />
        </ux-atoms-link>
      </li>
    </ul>
  </nav>
</template>
<script setup lang="ts">
// partially reused from Reused from https://gitlab.softfactory-accor.net/hotel/hotel-in-stay-services/wellness/wellness_apps/-/blob/dev/packages/front/src/components/ux/molecules/WlnsPagination/WlnsPagination.vue
export interface InspirationPaginationProps {
  currentPage: number;
  totalPage: number;
}

const props = withDefaults(defineProps<InspirationPaginationProps>(), {
  currentPage: 1,
  totalPage: 1
});

const emit = defineEmits(['inspiration-pagination::prev', 'inspiration-pagination::next', 'inspiration-pagination::goToPage']);

const { isTablet } = useCurrentWindowSize();

const previousPage = computed(() => {
  const previousPage = props.currentPage - 1 > 0 ? props.currentPage - 1 : null;
  return previousPage ? `?page=${previousPage}` : null;
});

const nextPage = computed(() => {
  const nextPage = props.currentPage + 1 <= props.totalPage ? props.currentPage + 1 : null;
  return nextPage ? `?page=${props.currentPage + 1}` : null;
});

const thisPage = (page: number) => {
  return `?page=${page}`;
};

const goToPage = (e: Event, page: number) => {
  if (props.currentPage === page) {
    e.preventDefault();
    return;
  }
  emit('inspiration-pagination::goToPage', page);
};

const goToPreviousPage = (e: Event) => {
  if (props.currentPage === 1) {
    e.preventDefault();
    return;
  }
  emit('inspiration-pagination::prev');
};

const goToNextPage = (e: Event) => {
  if (props.currentPage === props.totalPage) {
    e.preventDefault();
    return;
  }
  emit('inspiration-pagination::next');
};

const pagination = computed(() => {
  return !isTablet.value ? paginationSplitedPagesMobile.value : paginationSplitedPages.value;
});

const paginationSplitedPagesMobile = computed(() => {
  const pagination = [];

  // Print all pages if total page <= 3
  if (props.totalPage <= 5) {
    for (let i = 1; i <= props.totalPage; i++) {
      pagination.push(i);
    }

    return pagination;
  }

  // Always print first page button
  pagination.push(1);

  // Print "..." if currentPage is > 3
  if (props.currentPage > 3) {
    pagination.push('...');
  }

  // Print the 2 and 3 pages if current page matches the first and these and if total pages > 3
  if (props.currentPage === 1 || props.currentPage === 2 || props.currentPage === 3) {
    pagination.push(2);
    pagination.push(3);
  }

  // Print current page number button as long as it not the first or last page
  if (props.currentPage !== 1 && props.currentPage !== props.totalPage && props.currentPage > 3 && props.currentPage < props.totalPage - 2) {
    pagination.push(props.currentPage);
  }

  // Print "..." if currentPage is < lastPage -2
  if (props.currentPage < props.totalPage - 2) {
    pagination.push('...');
  }

  // Print the -2 and -1 pages if current page matches these and the last and if total pages > 3
  if (props.currentPage >= props.totalPage - 2) {
    pagination.push(props.totalPage - 2);
    pagination.push(props.totalPage - 1);
  }

  // Always print first last page button
  pagination.push(props.totalPage);

  return pagination;
});

const paginationSplitedPages = computed(() => {
  const pagination = [];
  const currentPage: number = props.currentPage;
  const firstPage: number = 1;
  const lastPage: number = props.totalPage;

  if (lastPage <= 6) {
    for (let i = 1; i <= lastPage; i++) {
      pagination.push(i);
    }

    return pagination;
  }

  if (currentPage <= 6) {
    for (let i = 1; i <= 6; i++) {
      pagination.push(i);
    }

    pagination.push('...');

    pagination.push(lastPage);

    return pagination;
  } else if (currentPage > 6 && currentPage < lastPage - 5) {
    pagination.push(firstPage);

    pagination.push('...');

    for (let i = currentPage - 3; i <= currentPage + 3; i++) {
      pagination.push(i);
    }

    pagination.push('...');

    pagination.push(lastPage);

    return pagination;
  } else {
    pagination.push(firstPage);

    pagination.push('...');

    for (let i = lastPage - 5; i <= lastPage; i++) {
      pagination.push(i);
    }

    return pagination;
  }
});
</script>
<style lang="scss" scoped>
@use 'InspirationPagination.scss';
</style>
